import authConstants from '../constants/auth';

/**
 * HTTPONLY COOKIES IMPLEMENTATION
 * 
 * This module implements a hybrid authentication system:
 * 1. PRIMARY: HttpOnly cookies (secure, XSS-protected)
 * 2. FALLBACK: localStorage (backward compatibility)
 * 
 * The system automatically detects which mode is active and handles
 * token storage/retrieval accordingly.
 */

// Authentication mode constants
const AUTH_MODE = {
  COOKIES: 'cookies',      // HttpOnly cookies (secure)
  LOCALSTORAGE: 'localStorage'  // Legacy mode (fallback)
};

/**
 * Gets the current authentication mode
 * Checks if we're using httpOnly cookies or localStorage
 * 
 * @returns {string} AUTH_MODE.COOKIES or AUTH_MODE.LOCALSTORAGE
 */
function getAuthMode() {
  try {
    const modeData = localStorage.getItem('auth_mode');
    if (modeData) {
      const parsed = JSON.parse(modeData);
      return parsed.mode || AUTH_MODE.COOKIES;
    }
  } catch (e) {
    console.warn('⚠️ Error reading auth mode:', e);
  }
  return AUTH_MODE.COOKIES;
}

/**
 * Sets the authentication mode
 * 
 * @param {string} mode - AUTH_MODE.COOKIES or AUTH_MODE.LOCALSTORAGE
 */
function setAuthMode(mode) {
  try {
    if (mode === AUTH_MODE.COOKIES) {
      // Ensure legacy token storage cannot re-enable Bearer auth behavior.
      localStorage.removeItem(authConstants.tokenString);
    }
    localStorage.setItem('auth_mode', JSON.stringify({ 
      mode, 
      timestamp: Date.now() 
    }));
  } catch (e) {
    console.error('❌ Error setting auth mode:', e);
  }
}

/**
 * Gets token data based on current authentication mode
 * 
 * COOKIES MODE: Returns minimal metadata (tokens are in httpOnly cookies)
 * LOCALSTORAGE MODE: Returns full token data from localStorage
 * 
 * @returns {Object} Token data object
 */
function getTokenData() {
  const mode = getAuthMode();
  
  if (mode === AUTH_MODE.COOKIES) {
    // In cookies mode, we only store metadata in localStorage
    // Actual tokens are in httpOnly cookies (not accessible to JS)
    try {
      const metadata = localStorage.getItem(authConstants.tokenMetadataString) || '{}';
      const parsed = JSON.parse(metadata);
      return {
        ...parsed,
        mode: AUTH_MODE.COOKIES,
        // Mark that tokens are in cookies
        tokensInCookies: true
      };
    } catch (e) {
      return { mode: AUTH_MODE.COOKIES, tokensInCookies: true };
    }
  } else {
    // Legacy localStorage mode
    try {
      let data = localStorage.getItem(authConstants.tokenString) || '{}';
      const parsed = JSON.parse(data);
      return {
        ...parsed,
        mode: AUTH_MODE.LOCALSTORAGE
      };
    } catch (e) {
      return { mode: AUTH_MODE.LOCALSTORAGE };
    }
  }
}

/**
 * Saves token data based on current authentication mode
 * 
 * COOKIES MODE: Only saves metadata (expires_in, last_refresh)
 *               Actual tokens are managed by backend in httpOnly cookies
 * LOCALSTORAGE MODE: Saves full token data including access_token and refresh_token
 * 
 * @param {Object} tokenInfo - Token information to save
 */
function saveTokenData(tokenInfo) {
  const mode = getAuthMode();
  
  if (mode === AUTH_MODE.COOKIES) {
    // In cookies mode, only save metadata (not the actual tokens)
    try {
      const metadata = {
        last_refresh: tokenInfo.last_refresh || Date.now(),
        expires_in: tokenInfo.expires_in,
        token_type: tokenInfo.token_type || 'bearer',
        mode: AUTH_MODE.COOKIES,
        tokensInCookies: true
      };
      localStorage.setItem(authConstants.tokenMetadataString, JSON.stringify(metadata));
    } catch (e) {
      console.error('❌ Error saving token metadata:', e);
    }
  } else {
    // Legacy localStorage mode - save everything
    try {
      localStorage.setItem(authConstants.tokenString, JSON.stringify(tokenInfo));
    } catch (e) {
      console.error('❌ Error saving token to localStorage:', e);
    }
  }
}

/**
 * Clears all authentication data
 * Works for both cookies and localStorage modes
 */
function clearTokenData() {
  try {
    localStorage.removeItem(authConstants.tokenString);
    localStorage.removeItem(authConstants.tokenMetadataString);
    localStorage.removeItem('auth_mode');
  } catch (e) {
    console.error('❌ Error clearing token data:', e);
  }
}

/**
 * Checks if we're currently using httpOnly cookies
 * 
 * @returns {boolean} true if using cookies, false if using localStorage
 */
function isUsingCookies() {
  return getAuthMode() === AUTH_MODE.COOKIES;
}

export { 
  getTokenData, 
  saveTokenData, 
  clearTokenData,
  getAuthMode,
  setAuthMode,
  isUsingCookies,
  AUTH_MODE 
};
